import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { bcEmit, bcSubscribe, CHAN_WISHLIST } from "../lib/broadcast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "jns_wishlist";

const WishlistContext = createContext();

const readLocal = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
};
const writeLocal = (ids) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch {}
};

export const WishlistProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [ids, setIds] = useState(() => readLocal());
    const mergedRef = useRef(false);
    const isRemoteUpdateRef = useRef(false);

    // Broadcast wishlist mutations to other tabs; skip when the update
    // itself came in from another tab to avoid an echo loop.
    useEffect(() => {
        if (isRemoteUpdateRef.current) {
            isRemoteUpdateRef.current = false;
            return;
        }
        bcEmit(CHAN_WISHLIST, ids);
    }, [ids]);

    // Subscribe to wishlist updates from sibling tabs
    useEffect(() => {
        return bcSubscribe(CHAN_WISHLIST, (remoteIds) => {
            if (!Array.isArray(remoteIds)) return;
            isRemoteUpdateRef.current = true;
            setIds(remoteIds);
            writeLocal(remoteIds);
        });
    }, []);

    // On login: merge local guest list into server list, then load server list
    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            // logged out — fall back to local
            mergedRef.current = false;
            setIds(readLocal());
            return;
        }
        if (mergedRef.current) return;
        mergedRef.current = true;
        const local = readLocal();
        const run = async () => {
            try {
                if (local.length > 0) {
                    const r = await axios.post(`${API}/wishlist/merge`, { ids: local }, { withCredentials: true });
                    const serverIds = r.data?.ids || [];
                    setIds(serverIds);
                    writeLocal(serverIds);
                } else {
                    const r = await axios.get(`${API}/wishlist`, { withCredentials: true });
                    const serverIds = r.data?.ids || [];
                    setIds(serverIds);
                    writeLocal(serverIds);
                }
            } catch {
                // network failure — keep local list
            }
        };
        run();
    }, [user, authLoading]);

    const has = useCallback((itemId) => ids.includes(itemId), [ids]);

    const add = useCallback(async (itemId) => {
        setIds((prev) => {
            if (prev.includes(itemId)) return prev;
            const next = [itemId, ...prev];
            writeLocal(next);
            return next;
        });
        if (user) {
            try { await axios.post(`${API}/wishlist`, { item_id: itemId }, { withCredentials: true }); } catch {}
        }
    }, [user]);

    const remove = useCallback(async (itemId) => {
        setIds((prev) => {
            const next = prev.filter((x) => x !== itemId);
            writeLocal(next);
            return next;
        });
        if (user) {
            try { await axios.delete(`${API}/wishlist/${itemId}`, { withCredentials: true }); } catch {}
        }
    }, [user]);

    const toggle = useCallback(async (itemId) => {
        if (ids.includes(itemId)) await remove(itemId);
        else await add(itemId);
    }, [ids, add, remove]);

    return (
        <WishlistContext.Provider value={{ ids, has, add, remove, toggle, count: ids.length }}>
            {children}
        </WishlistContext.Provider>
    );
};

export const useWishlist = () => useContext(WishlistContext);
